import { useState, useEffect } from 'react';
import { X, AlertTriangle, DollarSign, Mail, Phone, Users, Calendar, MapPin, Briefcase, Loader, ArrowDownRight, ArrowUpRight, CreditCard, Receipt, Info, ArrowLeft, MessageSquare } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import axios from 'axios';
import { toast } from 'sonner';

const CancelHiredJobModal = ({ job, jobDetails, isOpen, onClose, onSuccess }) => {
  const [refundType, setRefundType] = useState(null);
  const [chargeCrewFee, setChargeCrewFee] = useState(false);
  const [cancellationNotes, setCancellationNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [totalAmountPaid, setTotalAmountPaid] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch transactions when modal opens
  useEffect(() => {
    if (isOpen && job?.id) {
      fetchTransactions();
    }
  }, [isOpen, job?.id]);

  const fetchTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${job.id}/transactions`
      );
      const txns = response.data.transactions || [];
      setTransactions(txns);

      // Calculate total amount paid: sum of captured charges minus refunds
      let paid = 0;
      for (const txn of txns) {
        if (txn.type === 'charge' && txn.captured && txn.status === 'succeeded') {
          paid += (txn.amount - (txn.amount_refunded || 0));
        }
      }
      setTotalAmountPaid(paid / 100); // Convert cents to dollars
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      // Fall back to job total if transactions fail
      const fallbackAmount = parseFloat(job.budget || job.total_amount || jobDetails?.job?.budget || jobDetails?.job?.total_amount || 0);
      setTotalAmountPaid(fallbackAmount);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRefundType(null);
      setChargeCrewFee(false);
      setCancellationNotes('');
      setIsSubmitting(false);
      setShowConfirmation(false);
    }
  }, [isOpen]);

  if (!isOpen || !job) return null;

  const jobTotalAmount = job.budget || job.total_amount || jobDetails?.job?.budget || jobDetails?.job?.total_amount || 0;
  // Use actual paid amount for refund calculations
  const fullRefund = totalAmountPaid || parseFloat(jobTotalAmount) || 0;

  // Partial refund: 7% fee + 10% GST on fee = 7.7% retained
  const partialFeeExGst = fullRefund * 0.07;
  const partialFeeGst = partialFeeExGst * 0.10;
  const partialFeeTotal = partialFeeExGst + partialFeeGst;
  const partialRefund = fullRefund - partialFeeTotal;

  const hiredCrew = jobDetails?.hired_crew || [];

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleShowConfirmation = () => {
    if (!refundType) {
      toast.error('Please select a refund type');
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmCancel = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${job.id}/cancel-hired`,
        {
          refund_type: refundType,
          admin_email: 'hello@shootzu.com',
          actual_paid_amount: totalAmountPaid,
          charge_crew_cancellation_fee: refundType === 'full' ? chargeCrewFee : false,
          cancellation_notes: cancellationNotes.trim() || null
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Cancel job error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel job');
    } finally {
      setIsSubmitting(false);
    }
  };

  const owner = jobDetails?.owner;

  // Calculate crew fee amounts for display
  const getCrewFeeBreakdown = (crewPayout) => {
    const payout = parseFloat(crewPayout) || 0;
    const feeExGst = payout * 0.07;
    const gst = feeExGst * 0.10;
    const total = feeExGst + gst;
    return { feeExGst, gst, total };
  };

  // CONFIRMATION VIEW
  if (showConfirmation) {
    return (
      <div className="sz-modal-backdrop" style={{ zIndex: 200 }} onClick={() => !isSubmitting && setShowConfirmation(false)}>
        <div className="sz-modal-card w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ background: "linear-gradient(120deg,#B91C1C,#991B1B)" }}>
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-white" />
              <h3 className="sz-h3 text-white" style={{ fontSize: 18 }}>Confirm Cancellation</h3>
            </div>
          </div>

          {/* Confirmation Content */}
          <div className="szscroll flex-1 overflow-y-auto p-6 space-y-5">
            {/* Summary */}
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-5">
              <h4 className="text-lg font-bold text-red-800 mb-3">Cancellation Summary</h4>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Job:</span>
                  <span className="font-semibold text-gray-900">{job.title}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Refund Type:</span>
                  <Badge className={refundType === 'full' ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'}>
                    {refundType === 'full' ? 'Full Refund (100%)' : 'Partial Refund (92.3%)'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Refund to {owner?.name || 'Job Owner'}:</span>
                  <span className="text-lg font-bold text-red-600">
                    {formatCurrency(refundType === 'full' ? fullRefund : partialRefund)}
                  </span>
                </div>

                {refundType === 'partial' && (
                  <div className="mt-2 pt-2 border-t border-red-200">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>7% fee retained (ex GST):</span>
                      <span>{formatCurrency(partialFeeExGst)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>GST on fee (10%):</span>
                      <span>{formatCurrency(partialFeeGst)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold text-orange-700 mt-1">
                      <span>Total retained:</span>
                      <span>{formatCurrency(partialFeeTotal)}</span>
                    </div>
                  </div>
                )}

                {refundType === 'full' && chargeCrewFee && hiredCrew.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-red-200">
                    <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Crew Cancellation Fee Invoices</p>
                    {hiredCrew.map((crew, idx) => {
                      const fees = getCrewFeeBreakdown(crew.payout);
                      return (
                        <div key={idx} className="bg-white rounded p-2 mb-1 text-xs">
                          <div className="flex justify-between font-medium">
                            <span>{crew.name} ({crew.email})</span>
                            <span className="text-red-600">{formatCurrency(fees.total)}</span>
                          </div>
                          <div className="flex justify-between text-gray-400 mt-0.5">
                            <span>7% of {formatCurrency(crew.payout)} + GST</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {cancellationNotes.trim() && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Cancellation Notes</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{cancellationNotes.trim()}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions that will be taken */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">The following actions will be taken:</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">&#x2022;</span>
                  Job status will be set to <strong>CANCELLED</strong>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">&#x2022;</span>
                  Admin notification email sent to hello@shootzu.com for <strong>manual refund processing</strong>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">&#x2022;</span>
                  Cancellation details logged for audit and financial records
                </li>
                {refundType === 'partial' && (
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-0.5">&#x2022;</span>
                    Tax record invoice generated for the {formatCurrency(partialFeeTotal)} fee retained
                  </li>
                )}
                {refundType === 'full' && chargeCrewFee && (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">&#x2022;</span>
                      Cancellation fee invoice(s) sent to {hiredCrew.length} crew member(s)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">&#x2022;</span>
                      Crew member(s) platform access <strong>blocked</strong> until fee is paid
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">&#x2022;</span>
                      Reminder emails sent every <strong>48 hours</strong> until paid (CC hello@shootzu.com)
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Final warning */}
            <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-800 text-lg">Are you sure?</p>
                  <p className="text-sm text-red-700 mt-1">
                    This action cannot be undone. The job will be permanently cancelled and all parties will be notified.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 flex-shrink-0 flex items-center justify-between" style={{ borderTop: "1px solid #EEF0F4", background: "#FAFBFC" }}>
            <button onClick={() => setShowConfirmation(false)} disabled={isSubmitting} className="sz-btn sz-btn-outline">
              <ArrowLeft className="w-4 h-4" />Go Back
            </button>
            <button onClick={handleConfirmCancel} disabled={isSubmitting} className="sz-btn sz-btn-danger">
              {isSubmitting ? (<><Loader className="w-4 h-4 animate-spin" />Processing…</>) : (<><AlertTriangle className="w-4 h-4" />Confirm &amp; Cancel Job</>)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // MAIN FORM VIEW
  return (
    <div className="sz-modal-backdrop" style={{ zIndex: 200 }} onClick={onClose}>
      <div className="sz-modal-card w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ background: "linear-gradient(120deg,#DC2626,#B91C1C)" }}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <h3 className="sz-h3 text-white" style={{ fontSize: 18 }}>Cancel Hired Job</h3>
          </div>
          <button onClick={onClose} className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center text-white" style={{ background: "rgba(255,255,255,.18)" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="szscroll flex-1 overflow-y-auto p-6 space-y-5">
          {/* Job Details Card */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-purple-600" />
              Job Details
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Job ID:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {job.mysql_job_id || job.id?.substring(0, 8)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <Badge className="ml-2 bg-green-500 text-white text-xs">HIRED</Badge>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">Title:</span>
                <span className="ml-2 font-medium text-gray-900">{job.title}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span className="text-gray-500">Date:</span>
                <span className="ml-1 font-medium text-gray-900">
                  {job.date ? new Date(job.date).toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  }) : 'TBD'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-3 h-3 text-gray-400" />
                <span className="text-gray-500">Job Total:</span>
                <span className="ml-1 font-bold text-green-600">{formatCurrency(jobTotalAmount)}</span>
              </div>
              <div className="col-span-2 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-gray-400" />
                <span className="text-gray-500">Location:</span>
                <span className="ml-1 font-medium text-gray-900">{job.location || 'Not specified'}</span>
              </div>
            </div>
          </div>

          {/* Impacted People */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              Impacted People
            </h4>

            {/* Job Owner */}
            {owner && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Job Owner (Refund Recipient)</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{owner.name || 'Unknown'}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {owner.email || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {owner.phone || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Hired Crew */}
            {hiredCrew.length > 0 && (
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <p className="text-xs font-semibold text-green-700 uppercase mb-2">Hired Crew ({hiredCrew.length})</p>
                <div className="space-y-2">
                  {hiredCrew.map((crew, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white rounded p-2">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{crew.name || 'Unknown'}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{crew.email || 'N/A'}</span>
                          <span>{crew.phone || 'N/A'}</span>
                        </div>
                      </div>
                      {crew.payout && (
                        <span className="text-green-700 font-semibold text-sm">
                          {formatCurrency(crew.payout)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-purple-600" />
              Payment History
            </h4>

            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              {loadingTransactions ? (
                <div className="flex items-center justify-center py-4">
                  <Loader className="w-5 h-5 animate-spin text-purple-600 mr-2" />
                  <span className="text-sm text-gray-600">Loading transactions...</span>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-4">
                  <Receipt className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No Stripe payments found</p>
                  <p className="text-xs text-gray-500 mt-1">Using job total for refund calculation</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {transactions.map((txn, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-purple-100">
                      <div className="flex items-center gap-3">
                        {txn.type === 'charge' ? (
                          <ArrowDownRight className="w-4 h-4 text-green-600" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-red-600" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900 text-sm capitalize">{txn.type}</p>
                          <p className="text-xs text-gray-500">
                            {txn.created ? new Date(txn.created * 1000).toLocaleDateString('en-AU', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            }) : 'Unknown date'}
                            {txn.description && ` - ${txn.description}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold text-sm ${txn.type === 'charge' ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.type === 'charge' ? '+' : '-'}{formatCurrency((txn.amount || 0) / 100)}
                        </p>
                        <p className={`text-xs ${txn.status === 'succeeded' ? 'text-green-600' : 'text-gray-500'}`}>
                          {txn.captured === false ? 'Uncaptured' : txn.status === 'succeeded' ? 'Succeeded' : txn.status}
                          {txn.amount_refunded > 0 && (
                            <span className="text-orange-600 ml-1">
                              ({formatCurrency(txn.amount_refunded / 100)} refunded)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Total Amount Paid */}
              <div className="mt-3 pt-3 border-t border-purple-200">
                <div className="flex items-center justify-between bg-purple-100 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-700" />
                    <span className="font-semibold text-purple-900">Total Amount Paid:</span>
                  </div>
                  <span className="text-xl font-bold text-purple-700">{formatCurrency(totalAmountPaid)}</span>
                </div>
              </div>

              {/* Info Note */}
              <div className="mt-3 flex items-start gap-2 text-xs text-purple-700">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>Refund amount is calculated based on actual payments received, not the job total.</p>
              </div>
            </div>
          </div>

          {/* Refund Selection */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-red-600" />
              Select Refund Type <span className="text-red-500">*</span>
            </h4>

            <div className="space-y-2">
              {/* Full Refund Option */}
              <label
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  refundType === 'full'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="refundType"
                    value="full"
                    checked={refundType === 'full'}
                    onChange={() => { setRefundType('full'); setChargeCrewFee(false); }}
                    className="w-4 h-4 text-red-600 focus:ring-red-500"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Full Refund</p>
                    <p className="text-xs text-gray-500">Return 100% of amount paid to job owner</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-red-600">{formatCurrency(fullRefund)}</span>
              </label>

              {/* Crew Fee Checkbox (only shown when Full Refund is selected) */}
              {refundType === 'full' && (
                <div className="ml-11 mt-1 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={chargeCrewFee}
                      onChange={(e) => setChargeCrewFee(e.target.checked)}
                      className="w-4 h-4 mt-0.5 text-amber-600 focus:ring-amber-500 rounded"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        Invoice 7% cancellation fee + GST to crew member(s)
                      </p>
                      {hiredCrew.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {hiredCrew.map((crew, idx) => {
                            const fees = getCrewFeeBreakdown(crew.payout);
                            return (
                              <div key={idx} className="text-xs text-gray-500 flex justify-between">
                                <span>{crew.name}:</span>
                                <span>
                                  {formatCurrency(fees.feeExGst)} + GST {formatCurrency(fees.gst)} = <strong className="text-amber-700">{formatCurrency(fees.total)}</strong>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Crew will receive an invoice email with a Pay Now link. Platform access is blocked until paid. Reminder emails sent every 48 hours.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Partial Refund Option */}
              <label
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  refundType === 'partial'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="refundType"
                    value="partial"
                    checked={refundType === 'partial'}
                    onChange={() => { setRefundType('partial'); setChargeCrewFee(false); }}
                    className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Partial Refund</p>
                    <p className="text-xs text-gray-500">Return 92.3% of amount paid - 7% cancellation fee + GST retained</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-orange-600">{formatCurrency(partialRefund)}</span>
              </label>

              {/* GST Breakdown for Partial Refund */}
              {refundType === 'partial' && (
                <div className="ml-11 mt-1 p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-gray-600">
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>7% cancellation fee (ex GST):</span>
                      <span className="font-medium">{formatCurrency(partialFeeExGst)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST on fee (10%):</span>
                      <span className="font-medium">{formatCurrency(partialFeeGst)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-orange-700 pt-1 border-t border-orange-200">
                      <span>Total retained:</span>
                      <span>{formatCurrency(partialFeeTotal)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-gray-900 pt-1">
                      <span>Owner refund:</span>
                      <span>{formatCurrency(partialRefund)}</span>
                    </div>
                  </div>
                  <p className="text-gray-400 mt-2">A tax record invoice will be generated for the retained fee.</p>
                </div>
              )}
            </div>
          </div>

          {/* Cancellation Notes (optional) */}
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-600" />
              Cancellation Notes <span className="text-xs font-normal text-gray-500">(optional)</span>
            </h4>
            <textarea
              value={cancellationNotes}
              onChange={(e) => setCancellationNotes(e.target.value)}
              placeholder="Why was this job cancelled? (e.g. client double-booked, weather, crew unavailable)"
              rows={3}
              maxLength={2000}
              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-y"
            />
            <p className="text-xs text-gray-500">For internal reporting — not shown to crew or client.</p>
          </div>

          {/* Warning Banner */}
          <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800">This action cannot be undone</p>
                <p className="text-sm text-red-700 mt-1">
                  Cancelling this job will:
                </p>
                <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                  <li>Change the job status to CANCELLED</li>
                  <li>Send a refund notification to hello@shootzu.com</li>
                  <li>Log all cancellation details for audit purposes</li>
                  {refundType === 'partial' && (
                    <li>Generate a tax record invoice for the retained cancellation fee</li>
                  )}
                  {refundType === 'full' && chargeCrewFee && (
                    <>
                      <li>Generate and send cancellation fee invoice(s) to crew member(s)</li>
                      <li>Block crew platform access until fee is paid</li>
                    </>
                  )}
                </ul>
                <p className="text-sm text-red-700 mt-2 font-semibold">
                  No automatic Stripe refunds will be processed. Refund must be handled manually.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 flex-shrink-0 flex items-center justify-between" style={{ borderTop: "1px solid #EEF0F4", background: "#FAFBFC" }}>
          <button onClick={onClose} disabled={isSubmitting} className="sz-btn sz-btn-outline">Cancel</button>
          <button onClick={handleShowConfirmation} disabled={!refundType || isSubmitting} className="sz-btn sz-btn-danger">
            <AlertTriangle className="w-4 h-4" />Confirm Cancellation
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelHiredJobModal;
